import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { formatDistanceToNow } from 'date-fns';
import { Subscription, filter } from 'rxjs';

import {
  AppNotification,
  NotificationsService,
} from '../../../services/notifications/notifications.service';
import { AuthService } from '../../../services/auth/auth.service';
import { PushNotificationsService } from '../../../services/push-notifications/push-notifications.service';

const LIST_LIMIT = 15;
const BODY_MAX_LEN = 96;
const NAVBAR_DROPDOWN_OPEN = 'navbar-dropdown-open';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.css',
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  isOpen = false;
  isLoading = false;
  loadError = '';
  markingAll = false;
  deletingId: string | null = null;
  notifications: AppNotification[] = [];
  unreadCount = 0;
  isActive = false;

  private unreadSub?: Subscription;
  private listSub?: Subscription;
  private markSub?: Subscription;
  private deleteSub?: Subscription;
  private routeSub?: Subscription;
  private readonly onNavbarDropdownOpenBound = (event: Event) => {
    const detail = (event as CustomEvent<string>).detail;
    if (detail && detail !== 'notifications') {
      this.closePanel();
    }
  };

  constructor(
    private notificationsService: NotificationsService,
    private authService: AuthService,
    private pushNotificationsService: PushNotificationsService,
    private router: Router,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    window.addEventListener(NAVBAR_DROPDOWN_OPEN, this.onNavbarDropdownOpenBound);
    if (!this.authService.isAuthenticated()) {
      return;
    }
    this.unreadSub = this.notificationsService.unreadCount$.subscribe(
      (count) => {
        this.unreadCount = count;
      },
    );
    this.notificationsService.ensurePolling();
    this.pushNotificationsService.ensurePush();
    this.syncActive(this.router.url);
    this.routeSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.syncActive(e.urlAfterRedirects);
        if (this.isActive) {
          this.closePanel();
        }
      });
  }

  ngOnDestroy(): void {
    window.removeEventListener(NAVBAR_DROPDOWN_OPEN, this.onNavbarDropdownOpenBound);
    this.unreadSub?.unsubscribe();
    this.listSub?.unsubscribe();
    this.markSub?.unsubscribe();
    this.deleteSub?.unsubscribe();
    this.routeSub?.unsubscribe();
  }

  get isVisible(): boolean {
    return this.authService.isAuthenticated();
  }

  get badgeLabel(): string {
    if (this.unreadCount <= 0) return '';
    return this.unreadCount > 9 ? '9+' : String(this.unreadCount);
  }

  get bellAriaLabel(): string {
    if (this.unreadCount > 0) {
      return `Notifications, ${this.unreadCount} unread`;
    }
    return 'Notifications';
  }

  togglePanel(event?: Event): void {
    event?.stopPropagation();
    if (!this.isVisible) return;
    if (this.isOpen) {
      this.closePanel();
      return;
    }
    this.openPanel();
  }

  openPanel(): void {
    this.isOpen = true;
    window.dispatchEvent(
      new CustomEvent(NAVBAR_DROPDOWN_OPEN, { detail: 'notifications' }),
    );
    this.loadNotifications();
  }

  closePanel(): void {
    this.isOpen = false;
  }

  openFullPage(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closePanel();
    void this.router.navigate(['/notifications']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) {
      return;
    }
    this.closePanel();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.closePanel();
    }
  }

  loadNotifications(): void {
    this.listSub?.unsubscribe();
    this.isLoading = true;
    this.loadError = '';
    this.listSub = this.notificationsService
      .getNotifications({ page: 1, limit: LIST_LIMIT })
      .subscribe({
        next: (data) => {
          this.notifications = data.rows ?? [];
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.loadError = 'Could not load notifications.';
        },
      });
  }

  truncateBody(body: string | null | undefined): string {
    const text = (body || '').trim();
    if (text.length <= BODY_MAX_LEN) return text;
    return `${text.slice(0, BODY_MAX_LEN - 1)}…`;
  }

  onRowClick(notification: AppNotification, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const id = notification.notification_id;
    const goToPage = () => {
      this.closePanel();
      void this.router.navigate(['/notifications'], {
        queryParams: { id },
      });
    };

    if (notification.is_read) {
      goToPage();
      return;
    }

    this.markSub?.unsubscribe();
    this.markSub = this.notificationsService.markAsRead(id).subscribe({
      next: () => {
        notification.is_read = true;
        goToPage();
      },
      error: () => goToPage(),
    });
  }

  onMarkAllAsRead(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.markingAll || this.unreadCount <= 0) return;

    this.markingAll = true;
    this.markSub?.unsubscribe();
    this.markSub = this.notificationsService.markAllAsRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map((n) => ({
          ...n,
          is_read: true,
        }));
        this.markingAll = false;
      },
      error: () => {
        this.markingAll = false;
      },
    });
  }

  onDeleteOne(notification: AppNotification, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.deletingId) return;

    this.deletingId = notification.notification_id;
    this.deleteSub?.unsubscribe();
    this.deleteSub = this.notificationsService
      .deleteNotification(notification.notification_id)
      .subscribe({
        next: () => {
          this.notifications = this.notifications.filter(
            (n) => n.notification_id !== notification.notification_id,
          );
          this.deletingId = null;
        },
        error: () => {
          this.deletingId = null;
        },
      });
  }

  relativeTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return '';
    }
  }

  trackById(_index: number, item: AppNotification): string {
    return item.notification_id;
  }

  private syncActive(url: string): void {
    this.isActive = url.split('?')[0] === '/notifications';
  }
}
